import { useAuth } from "@hl/shared-features/lib/features/auth/AuthContext";
import { logError } from "@hl/shared-features/lib/services/logger";
import dayjs, { extend } from "dayjs";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import { useNavigate } from "react-router-dom";

import {
  _CollectionType,
  DraftProgress,
  UpdateCollectionDraftInput,
} from "apollo/graphql.generated";
import { TOOLS_URL } from "config";
import { collectionTypeToDisplayName } from "~/features/collections/displayUtils";
import {
  CreateCollectionForm,
  useCreateCollectionFormContext,
  useCreateCollectionState,
} from "~/features/collections/forms/CreateCollectionMultistepForm/context";
import {
  useCreateCollectionDraftMutation,
  useUpdateCollectionDraftMutation,
} from "~/features/collections/queries/draft.graphql.generated";

extend(LocalizedFormat);

interface UseSaveDraftProps {
  furthestStep?: DraftProgress;
}

const useSaveDraft = ({
  furthestStep = DraftProgress.SELECTED_TYPE,
}: UseSaveDraftProps) => {
  const { walletAddress } = useAuth();
  const navigate = useNavigate();

  const {
    dispatch,
    state: { draftId, currentStep, steps },
  } = useCreateCollectionState();
  const form = useCreateCollectionFormContext();
  const formValues = form.values;

  const [createCollectionDraftMutation, { loading: createLoading }] =
    useCreateCollectionDraftMutation();

  const [updateCollectionDraftMutation, { loading: updateLoading }] =
    useUpdateCollectionDraftMutation();

  const onDraftUpdate = async (draftId: string) => {
    const draft = await updateCollectionDraftMutation({
      variables: {
        draftId,
        updateCollectionDraftData: draftFormToEntity(
          formValues,
          furthestStep,
          walletAddress
        ),
      },
    });
    if (draft.data) {
      const collectionDraft = draft.data.updateCollectionDraft;
      // Rewrites the URL with the newly created draftId
      navigate(
        TOOLS_URL.collections.resumeCreate.replace(
          ":collectionId",
          collectionDraft.id
        ),
        { replace: true }
      );

      dispatch({
        type: "UPDATE_DRAFT",
        payload: {
          draftId: collectionDraft.id,
          unzipped: collectionDraft.metadata?.seriesS3Unzipped ?? false,
          draftProgress: collectionDraft.furthestStep,
        },
      });
    }
  };

  const saveDraft = async () => {
    if (steps[currentStep].visited) {
      form.validate();
    }
    if (draftId) {
      onDraftUpdate(draftId);
    } else {
      const draft = await createCollectionDraftMutation({
        variables: {
          createCollectionDraftData: {
            collectionType: form.values.collectionType,
          },
        },
      });
      const draftId = draft.data?.createCollectionDraft.draft.id;
      if (draft.errors || !draftId) {
        logError(
          { e: draft.errors },
          `Failed to create a draft. draftId: ${draftId}`
        );
      } else {
        dispatch({
          type: "CREATE_DRAFT",
          payload: {
            draftId,
          },
        });
        onDraftUpdate(draftId);
      }
    }
    form.resetDirty();
  };

  return {
    saveDraft,
    loading: createLoading || updateLoading,
  };
};

export const draftFormToEntity = (
  formValues: CreateCollectionForm,
  furthestStep: DraftProgress,
  walletAddress?: string,
  isLastStep = false
): UpdateCollectionDraftInput => ({
  furthestStep,
  gateId: formValues.gateId || null,
  collectionType: formValues.collectionType,
  distributionDraft: formValues.withoutMintVector
    ? null
    : {
        type: formValues.distribution.type,
        price: formValues.distribution.price?.toString() || null,
        currency: formValues.distribution.currency,
        saleDateFrom: formValues.distribution.saleDateFrom.toISOString(),
        saleDateTo: formValues.distribution.saleDateTo?.toISOString() || null,
        priceType: formValues.distribution.priceType,
        scheduledPrices: formValues.distribution.scheduledPrices,
        priceDropInterval: formValues.distribution.priceDropInterval,
        isDirectMint: formValues.distribution.isDirectMint,
        minBid: formValues.distribution.minBid?.toString(),
        rebateWinningBids: formValues.distribution.rebateWinningBids,
        sponsoredMintsEnabled: formValues.distribution.sponsoredMintsEnabled,
      },
  metadata: {
    mediaTypeStr: formValues.metadata.mediaType,
    mediaUrl: formValues.metadata.mediaUrl || null,
    mediaFiles: formValues.metadata.mediaFiles ?? null,
    traits: (formValues.metadata.traits
      ? Object.keys(formValues.metadata.traits).map((key) => {
          return {
            traitName: key,
            traitValue: formValues.metadata.traits[key],
          };
        })
      : []
    ).concat(
      isLastStep &&
        formValues.metadata.generateNameTrait &&
        !formValues.metadata.traits["name"]
        ? [{ traitValue: formValues.collection.name ?? "", traitName: "name" }]
        : []
    ),
    generateNameTrait: formValues.metadata.generateNameTrait,
    coverMediaUrl: formValues.metadata.coverMediaUrl || undefined,
    reveal: formValues.metadata.reveal,
  },
  collectionDraft: {
    name:
      formValues.collection.name ||
      `${collectionTypeToDisplayName(
        formValues.collectionType
      )} ${dayjs().format("LL")}`,
    description: formValues.collection.description || null,
    size: formValues.collection.size,
    // https://linear.app/highlight-xyz/issue/ENG-5044/[frontend]-save-series-collection-details-while-saving-metadata
    seriesDetails:
      formValues.collectionType === _CollectionType.Series
        ? {
            mintPreviewImageUrls: formValues.metadata.previewImages.map(
              (previewImage) => previewImage.url
            ),
            showAllMetadata: formValues.metadata.showAllMetadata,
          }
        : undefined,
    generativeDetails:
      formValues.collectionType === _CollectionType.GenerativeSeries
        ? {
            ...formValues.generativeDetails,
            allowedHashes: formValues.generativeDetails?.allowedHashes?.length
              ? formValues.generativeDetails.allowedHashes
              : null,
          }
        : undefined,
    categories: formValues.collection.categories?.length
      ? formValues.collection.categories
      : undefined,
    tags: formValues.collection.tags?.length
      ? formValues.collection.tags
      : undefined,
  },
  moduleSettings: {
    nonTransferable: {
      active: formValues.moduleSettings.nonTransferable,
    },
    limitedMints:
      formValues.moduleSettings.shouldLimitNumberOfMintPerWallet &&
      formValues.moduleSettings.limitPerWallet
        ? {
            limitPerWallet: formValues.moduleSettings.limitPerWallet,
          }
        : null,
    royalty: {
      // Royalty amount is in BPS (integer)
      royaltyAmount: Math.round(
        formValues.moduleSettings.royalty.royaltyAmount * 100
      ),
      payoutEnsName: formValues.moduleSettings.royalty.payoutEnsName,
      payoutEoa: formValues.moduleSettings.royalty.payoutEoa || null,
    },
    customPaymentRecipient: {
      eoa:
        formValues.moduleSettings.customPaymentRecipient.eoa || walletAddress,
      ensName: formValues.moduleSettings.customPaymentRecipient.ensName,
    },
  },
  contractDraft:
    formValues.contract.type === "new"
      ? {
          chainId:
            formValues.collection.chainId &&
            parseInt(formValues.collection.chainId, 10)
              ? parseInt(formValues.collection.chainId, 10)
              : 11155111,
          name: formValues.contract.data.name,
          symbol: formValues.contract.data.symbol,
          usingOpenseaBlocklist: formValues.contract.data.usingOpenseaBlocklist,
          collectionLogoImage: formValues.contract.data.collectionLogoImage,
        }
      : null,
  contractId:
    formValues.contract.type === "existing"
      ? formValues.contract.data.contractId
      : null,
});

export default useSaveDraft;
export type UseSaveDraft = ReturnType<typeof useSaveDraft>;
